import { useState, useEffect, useRef } from 'react';
import { fetchArtworkFromAllSources } from '../data/indexer';
import './HomePage.css';

// Helper to shorten addresses
const fmt = (a) => `${a.slice(0, 6)}...${a.slice(-4)}`;

// Real MetaMask wallet connection
function WalletPill({ onLoad }) {
  const [connected, setConnected] = useState(false);
  const [addr, setAddr] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    // Check if wallet is already connected on mount
    checkIfWalletIsConnected();

    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Check if MetaMask is installed and wallet is already connected
  const checkIfWalletIsConnected = async () => {
    if (typeof window.ethereum === 'undefined') {
      console.log('MetaMask is not installed');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setAddr(accounts[0]);
        setConnected(true);
      }
    } catch (error) {
      console.error('Error checking wallet connection:', error);
    }
  };

  // Connect wallet
  const connect = async () => {
    if (typeof window.ethereum === 'undefined') {
      alert('MetaMask is not installed. Please install MetaMask to connect your wallet.');
      return;
    }

    try {
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      if (accounts.length > 0) {
        setAddr(accounts[0]);
        setConnected(true);
        setMenuOpen(false);
        console.log('✅ Wallet connected:', accounts[0]);
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      if (error.code === 4001) {
        // User rejected the request
        alert('Connection rejected. Please approve the connection in MetaMask.');
      }
    }
  };

  // Disconnect wallet
  const disconnect = () => {
    setConnected(false);
    setAddr('');
    setMenuOpen(false);
    console.log('🔌 Wallet disconnected');
  };

  // Listen for account changes
  useEffect(() => {
    if (typeof window.ethereum !== 'undefined') {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          // User disconnected their wallet
          disconnect();
        } else if (accounts[0] !== addr) {
          // User switched accounts
          setAddr(accounts[0]);
          setConnected(true);
          console.log('🔄 Account changed:', accounts[0]);
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [addr]);

  return (
    <div className="wallet-wrapper" ref={menuRef}>
      <button className="wallet-pill" onClick={() => setMenuOpen((v) => !v)} aria-haspopup="menu">
        <span className="wallet-fox" aria-hidden>
          ▲
        </span>
        {connected ? fmt(addr) : 'Connect Wallet'}
      </button>

      {menuOpen && (
        <div className="wallet-menu" role="menu">
          {!connected ? (
            <button className="wallet-menu-item" onClick={connect}>
              Connect MetaMask
            </button>
          ) : (
            <>
              <div className="wallet-menu-address">{addr}</div>
              <button className="wallet-menu-item" onClick={() => onLoad?.()}>
                Load My Artworks
              </button>
              <button
                className="wallet-menu-item subtle"
                onClick={disconnect}
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Loader
function CollectingStories({ show, onClose }) {
  if (!show) return null;
  return (
    <div className="stories-overlay" onClick={onClose}>
      <div className="stories-card" onClick={(e) => e.stopPropagation()}>
        <div className="stories-spinner" aria-hidden />
        <h2 className="stories-title">collecting stories…</h2>
        <p className="stories-sub">
          On-chain provenance · Off-chain whispers · Threads of history
        </p>
        <button className="stories-cancel" onClick={onClose}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

// Creator Context Modal - only shown when wallet is connected
function CreatorContextModal({ show, onSubmit, onClose, address }) {
  const [creatorNote, setCreatorNote] = useState('');

  if (!show) return null;

  const handleSubmit = () => {
    onSubmit(creatorNote); // pass the note up
  };

  const handleSkip = () => {
    onSubmit(''); // Submit with empty note
  };

  return (
    <div className="stories-overlay" onClick={onClose}>
      <div className="stories-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="stories-title">Connected as Creator</h2>
        <p className="stories-sub">Add context about this artwork (optional)</p>
        <div className="wallet-menu-address" style={{ marginBottom: '12px' }}>
          {address}
        </div>

        <textarea
          placeholder="Write about your artwork, process, or intent…"
          value={creatorNote}
          onChange={(e) => setCreatorNote(e.target.value)}
          style={{
            width: '100%',
            minHeight: '110px',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.06)',
            color: '#f3f3fb',
            padding: '10px 12px',
            marginBottom: '18px',
            resize: 'vertical',
          }}
        />

        <button className="wallet-menu-item" onClick={handleSubmit}>
          Submit Context
        </button>
        <button className="stories-cancel" onClick={handleSkip}>
          Skip
        </button>
        <button className="stories-cancel" onClick={onClose} style={{ marginTop: '8px' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}


export default function HomePage({ onNavigateToStream }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [connectedWallet, setConnectedWallet] = useState(null);

  // Listen for wallet connection status
  useEffect(() => {
    const checkWallet = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            setConnectedWallet(accounts[0]);
          }
        } catch (error) {
          console.error('Error checking wallet:', error);
        }
      }
    };

    checkWallet();

    // Listen for account changes
    if (typeof window.ethereum !== 'undefined') {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setConnectedWallet(accounts[0]);
        } else {
          setConnectedWallet(null);
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Validate contract address format
    if (!searchQuery.trim()) {
      setFetchError('Please enter a contract address');
      return;
    }
    
    // Basic validation for Ethereum address format
    if (!/^0x[a-fA-F0-9]{40}$/.test(searchQuery.trim())) {
      setFetchError('Please enter a valid Ethereum contract address (0x followed by 40 hex characters)');
      return;
    }
    
    setFetchError(null);
    
    // If wallet is connected, show creator context modal
    // Otherwise, go straight to loading and fetching
    if (connectedWallet) {
      console.log('💼 Wallet connected - showing creator context modal');
      setShowContextModal(true);
    } else {
      console.log('🔓 No wallet connected - fetching directly');
      handleContextSubmit(''); // Empty creator note
    }
  };

  const handleContextSubmit = async (creatorNote) => {
    console.log('Creator context submitted:', creatorNote);
    console.log('Connected wallet:', connectedWallet);

    setShowContextModal(false);
    setShowLoader(true);

    try {
      // Fetch artwork data from all sources
      console.log('🎨 Fetching artwork for contract:', searchQuery);
      const artworkData = await fetchArtworkFromAllSources(searchQuery.trim());
      
      console.log('✅ Artwork data fetched:', artworkData);
      
      // Check if we got any image
      if (!artworkData.image_url) {
        console.warn('⚠️ No image found for this contract address');
        setFetchError('No artwork image found for this contract address');
        setShowLoader(false);
        return;
      }

      // Navigate to stream with the artwork data
      setTimeout(() => {
        setShowLoader(false);
        onNavigateToStream(artworkData, creatorNote);
      }, 1500);
      
    } catch (error) {
      console.error('❌ Error fetching artwork:', error);
      setFetchError(error.message || 'Failed to fetch artwork data');
      setShowLoader(false);
    }
  };

  const handleLoadWalletArtworks = () => {
    // This function can be called from the wallet menu
    // to load artworks owned by the connected wallet
    if (connectedWallet) {
      setSearchQuery(connectedWallet);
      // Optionally trigger search automatically
      // handleContextSubmit('');
    }
  };

  return (
    <div className="homepage">
      {/* Top-right wallet */}
      <div className="topbar">
        <WalletPill onLoad={handleLoadWalletArtworks} />
      </div>

      <div className="wavy-background"></div>

      <div className="homepage-content">
        <div className="left-column">
          <div className="title-section">
            <h1 className="main-title">stream-of-consciousness</h1>
            <p className="subtitle">
              Immerse yourself in the on-chain and off-chain stories of any artwork
            </p>
          </div>

          <div className="search-section">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Enter contract address (0x...)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setFetchError(null); // Clear error on input change
                  }}
                  className="search-input"
                />

                <button type="submit" className="search-button">
                  Search
                </button>
              </div>

              {/* Connection status indicator */}
              {connectedWallet && (
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(76, 175, 80, 0.15)',
                  border: '1px solid rgba(76, 175, 79, 0.78)',
                  borderRadius: '8px',
                  color: '#173918ff',
                  fontSize: '0.85rem',
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>✓</span>
                  <span>Wallet connected - you can add creator context</span>
                </div>
              )}

              {/* Error message display */}
              {fetchError && (
                <div style={{
                  padding: '10px 15px',
                  background: 'rgba(255, 59, 48, 0.15)',
                  border: '1px solid rgba(255, 59, 48, 0.3)',
                  borderRadius: '8px',
                  color: '#ff3b30',
                  fontSize: '0.9rem',
                  marginTop: '10px'
                }}>
                  {fetchError}
                </div>
              )}

              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Upload preview" />
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedImage(null);
                      setImagePreview(null);
                    }}
                    className="remove-image"
                  >
                    ✕
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="right-column">
          <div className="about-section">
            <h2>About</h2>
            <p>
              <strong>
                stream-of-consciousness: An immersive metaverse art discovery engine curating
                off-chain and on-chain stories of every artwork ever created.
              </strong>
            </p>
            <p>
              Every artwork deserves comprehensive curation. Navigate through a river of information,
              discovering on-chain provenance and off-chain context that traditional platforms miss and
              get credited for contributions to awakening artworks.
            </p>
            <p>
              <strong>How to use:</strong> Enter an Ethereum contract address (starting with 0x) 
              to discover and explore the complete story behind any NFT artwork.
            </p>
            <p>
              <strong>Creator Mode:</strong> Connect your wallet to add creator context and notes 
              to artworks you've created. <strong>Wallet connection is optional for browsing.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreatorContextModal
        show={showContextModal}
        address={connectedWallet}
        onSubmit={handleContextSubmit}
        onClose={() => setShowContextModal(false)}
      />
      <CollectingStories show={showLoader} onClose={() => setShowLoader(false)} />
    </div>
  );
}